import { produce } from 'immer';
import { create } from 'zustand';

import type {
  AnnotationsListResponse,
  ApiError,
  ChaptersListResponse,
  PatchedVideoRequest,
  Video,
} from '@double.us/api';
import { assets } from '@double.us/core/config/absolute-url';

import { client } from '../services/client';

export interface UploadState {
  progress: number;
  status: 'uploading' | 'failed' | 'processing' | 'completed';
  cancel: () => void;
}

export interface VideoStoreState {
  videos: Record<string, Video>;
  annotations: AnnotationsListResponse;
  chapters: ChaptersListResponse;
  isLoading: boolean;
  isDeleting: boolean;
  isFetchedOnce: boolean;
  uploading: Record<string, UploadState>;
  error: ApiError | null;
  fetchVideos: () => Promise<void>;
  fetchVideo: (key: string) => Promise<void>;
  fetchVideoData: (key: string) => Promise<void>;
  updateVideo: (key: string, body: PatchedVideoRequest) => Promise<void>;
  isUpdateingVideo: string | null; // key of the video being updated
  uploadVideo: (file: File) => Promise<Video | undefined>;
  setThumbnail: (key: string, file: File) => Promise<void>;
  deleteVideo: (key: string) => Promise<void>;
  resetUploadPage: () => void;
}

interface UploadParams {
  url: string;
  file: File;
  onProgress: (progress: number) => void;
  signal: AbortSignal;
}

// This is a private helper function and is not part of the store's public API.
export const _upload = ({ url, file, onProgress, signal }: UploadParams): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded * 100) / event.total);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    signal.addEventListener('abort', () => {
      xhr.abort();
    });

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.send(file);
  });
};

export const useVideoStore = create<VideoStoreState>(set => ({
  // Initial State
  videos: {},
  annotations: [],
  chapters: [],
  isLoading: false,
  isDeleting: false,
  isFetchedOnce: false,
  uploading: {},
  error: null,
  isUpdateingVideo: null,
  // Actions
  fetchVideos: async () => {
    set({ isLoading: true, error: null });

    const { data, error } = await client.videosList();

    if (error) {
      set({ error, isLoading: false, isFetchedOnce: true });
      return;
    }

    const videos = data.reduce(
      (acc, video) => {
        acc[video.key] = video;
        return acc;
      },
      {} as unknown as Record<string, Video>,
    );

    set({ videos, isLoading: false, isFetchedOnce: true });
  },
  fetchVideo: async key => {
    set({ isLoading: true, error: null });

    const { data: video, error } = await client.videosRetrieve({
      path: { key },
    });

    if (error) {
      set({ error, isLoading: false });
      return;
    }

    set(
      produce((state: VideoStoreState) => {
        state.videos[video.key] = video;
        state.isLoading = false;
      }),
    );
  },

  fetchVideoData: async key => {
    set({ isLoading: true, error: null });
    try {
      const [annotationsResponse, chaptersResponse] = await Promise.all([
        client.videosAnnotationsList({ path: { key } }),
        client.videosChaptersList({ path: { key } }),
      ]);

      if (annotationsResponse.error || chaptersResponse.error) {
        throw new Error('Failed to fetch video data');
      }

      set(
        produce((state: VideoStoreState) => {
          state.annotations = annotationsResponse.data;
          state.chapters = chaptersResponse.data;
          state.isLoading = false;
        }),
      );
    } catch (error) {
      set({ error: error as ApiError, isLoading: false });
    }
  },

  updateVideo: async (key, body) => {
    set({ isUpdateingVideo: key, error: null });

    const { data: updatedVideo, error } = await client.videosPartialUpdate({ path: { key }, body });

    if (error) {
      set({ error, isUpdateingVideo: null });
      return;
    }

    set(
      produce((state: VideoStoreState) => {
        state.videos[updatedVideo.key] = updatedVideo;
        state.isUpdateingVideo = null;
      }),
    );
  },
  uploadVideo: async file => {
    set({ isLoading: true });
    const { data, error } = await client.videosCreate({
      body: { filename: file.name },
    });
    if (error) {
      set({ error: error as unknown as ApiError, isLoading: false });
      return undefined;
    }

    const { video, signed_url } = data;
    const ctrl = new AbortController();
    set(
      produce((state: VideoStoreState) => {
        state.videos[video.key] = video;
        state.uploading[video.key] = {
          status: 'uploading',
          progress: 0,
          cancel: () => ctrl.abort(),
        };
      }),
    );

    void _upload({
      url: signed_url,
      file,
      signal: ctrl.signal,
      onProgress: progress => {
        set(
          produce((state: VideoStoreState) => {
            state.uploading[video.key].progress = progress;
          }),
        );
      },
    })
      .then(() => {
        set(
          produce((state: VideoStoreState) => {
            state.uploading[video.key].status = 'completed';
          }),
        );
      })
      .catch(() => {
        set(
          produce((state: VideoStoreState) => {
            state.uploading[video.key].status = 'failed';
          }),
        );
      });

    set({ isLoading: false });

    return video;
  },
  setThumbnail: async (key, file) => {
    set({ isLoading: true });
    const { data, error } = await client.assetsCreate({
      body: { filename: file.name },
    });

    if (error) {
      set({ error: error as unknown as ApiError, isLoading: false });
      return undefined;
    }

    const { path, signed_url } = data;
    const filePath = assets(path);
    const uploadKey = `${key}-thumbnail`;
    const ctrl = new AbortController();

    set(
      produce((state: VideoStoreState) => {
        state.uploading[uploadKey] = {
          status: 'uploading',
          progress: 0,
          cancel: () => ctrl.abort(),
        };
      }),
    );

    void _upload({
      url: signed_url,
      file,
      signal: ctrl.signal,
      onProgress: progress => {
        set(
          produce((state: VideoStoreState) => {
            state.uploading[uploadKey].progress = progress;
          }),
        );
      },
    })
      .then(async () => {
        const { error } = await client.videosPartialUpdate({
          path: { key },
          body: { thumbnail: filePath },
        });

        if (error) {
          set({ error });
          return;
        }

        set(
          produce((state: VideoStoreState) => {
            state.videos[key].thumbnail = filePath;
            state.uploading[uploadKey].status = 'completed';
          }),
        );
      })
      .catch(() => {
        set(
          produce((state: VideoStoreState) => {
            state.uploading[uploadKey].status = 'failed';
          }),
        );
      });

    set({ isLoading: false });
  },
  deleteVideo: async (key: string) => {
    set({ isDeleting: true });
    const { data, error } = await client.videosDestroy({
      path: { key: key },
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (data) {
      set(
        produce((state: VideoStoreState) => {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete state.videos[key];
          state.isDeleting = false;
        }),
      );
    }
    if (error) {
      set({ error: error as unknown as ApiError, isLoading: false });
      return undefined;
    }
  },
  resetUploadPage: () => {
    set({ isLoading: false, uploading: {}, error: null });
  },
}));
